import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  PIPELINE_STAGES,
  defaultBoard,
  formatRs,
  formatSpot,
  pipelineStageStatus,
  utcClockString
} from './degen-engine';
import './DegenDashboard.css';
import './CncDegenDashboard.css';
import DegenActionScreen from './DegenActionScreen';
import IstClock from '../IstClock';
import { formatIstDateTime, formatActivityWhen } from '../ist-datetime';
import TradingCardWhen from '../TradingCardWhen';
import '../TradingActionBoard.css';

const POLL_MS = 2000;

function WinStreakRing({ streak, nextSec }) {
  const pct = Math.min(100, (streak / 50) * 100);
  return (
    <div className="degen-streak-ring" aria-label={`${streak} win streak`}>
      <svg viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="52" className="degen-ring-bg" />
        <circle
          cx="60"
          cy="60"
          r="52"
          className="degen-ring-fill"
          strokeDasharray={`${pct * 3.27} 999`}
          transform="rotate(-90 60 60)"
        />
      </svg>
      <div className="degen-streak-inner">
        <strong>{streak}</strong>
        <span>WIN STREAK</span>
        <em>NEXT IN {nextSec}s</em>
      </div>
    </div>
  );
}

function WinStackChart({ data }) {
  const max = Math.max(...(data || []).map((d) => d.value), 1);
  return (
    <div className="degen-win-stack">
      {(data || []).map((bar) => (
        <div key={bar.label} className="degen-stack-bar-wrap" title={formatRs(bar.value)}>
          <div className="degen-stack-bar" style={{ height: `${(bar.value / max) * 100}%` }} />
        </div>
      ))}
    </div>
  );
}

function Pnl24Chart({ series }) {
  const rows = series?.length ? series : [{ t: 0, v: 0 }];
  const w = 420;
  const h = 140;
  const min = Math.min(...rows.map((p) => p.v));
  const max = Math.max(...rows.map((p) => p.v));
  const range = max - min || 1;
  const points = rows.map((p, i) => {
    const x = rows.length > 1 ? (i / (rows.length - 1)) * w : w / 2;
    const y = h - ((p.v - min) / range) * (h - 12) - 6;
    return `${x},${y}`;
  }).join(' ');
  const area = `0,${h} ${points} ${w},${h}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="degen-pnl24-svg" preserveAspectRatio="none">
      <defs>
        <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(34, 120, 72, 0.45)" />
          <stop offset="100%" stopColor="rgba(34, 120, 72, 0.02)" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#pnlGrad)" />
      <polyline points={points} fill="none" stroke="#1a6b42" strokeWidth="2" />
    </svg>
  );
}

function CandlestickChart({ candles, price, symbol }) {
  const rows = candles?.length ? candles : [];
  const w = 640;
  const h = 200;
  if (!rows.length) {
    return (
      <div className="degen-candle-wrap">
        <div className="degen-spot-head">
          <span>LIVE {symbol || 'NIFTY'} · NSE</span>
          <strong>{formatSpot(price)}</strong>
        </div>
        <p className="degen-chart-loading">Loading live chart…</p>
      </div>
    );
  }
  const lows = rows.map((c) => c.low);
  const highs = rows.map((c) => c.high);
  const min = Math.min(...lows);
  const max = Math.max(...highs);
  const range = max - min || 1;
  const barW = w / rows.length;

  return (
    <div className="degen-candle-wrap">
      <div className="degen-spot-head">
        <span>LIVE {symbol || 'NIFTY'} · NSE INDEX</span>
        <strong className={rows[rows.length - 1]?.bull ? 'up' : 'down'}>{formatSpot(price)}</strong>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="degen-candle-svg" preserveAspectRatio="none">
        {rows.map((c, i) => {
          const x = i * barW + barW * 0.2;
          const bw = barW * 0.6;
          const y = (v) => h - ((v - min) / range) * (h - 16) - 8;
          const color = c.bull ? '#1a7a4a' : '#b83232';
          return (
            <g key={`c-${i}`}>
              <line x1={x + bw / 2} y1={y(c.high)} x2={x + bw / 2} y2={y(c.low)} stroke={color} strokeWidth="1" />
              <rect x={x} y={Math.min(y(c.open), y(c.close))} width={bw} height={Math.max(2, Math.abs(y(c.open) - y(c.close)))} fill={color} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function PipelineStrip({ state }) {
  return (
    <section className="degen-pipeline">
      <header>
        <h3>6-Cycle Execution Pipeline</h3>
        <span>NSE + BSE · momentum scan → paper settle</span>
      </header>
      <div className="degen-pipeline-row">
        {PIPELINE_STAGES.map((stage, i) => {
          const status = pipelineStageStatus(state.activeStage, i);
          const meta = state.cycleMeta?.[stage.key] || {};
          return (
            <div key={stage.key} className={`degen-pipe-card ${status}`}>
              <div className="degen-pipe-num">{stage.id}</div>
              <strong>{stage.label}</strong>
              <p>{meta.detail || stage.desc}</p>
              <small>{meta.sub || (status === 'active' ? `${Math.round(state.stageProgress || 0)}%` : '—')}</small>
              {status === 'active' && (
                <div className="degen-pipe-progress">
                  <div style={{ width: `${state.stageProgress || 0}%` }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ScanTable({ rows }) {
  if (!rows?.length) {
    return <p className="degen-scan-empty">Scanning NSE + BSE universe…</p>;
  }
  return (
    <div className="degen-scan-table-wrap">
      <table className="degen-scan-table">
        <thead>
          <tr>
            <th>Stock</th>
            <th>Exch</th>
            <th>Price</th>
            <th>Δ%</th>
            <th>RSI</th>
            <th>Score</th>
            <th>Dir</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 8).map((r) => (
            <tr key={r.symbol} className={r.qualifies ? 'qualifies' : ''}>
              <td>{r.stock}</td>
              <td><span className={`degen-exch degen-exch-${(r.exchange || 'NSE').toLowerCase()}`}>{r.exchange}</span></td>
              <td>₹{Number(r.price).toFixed(2)}</td>
              <td className={r.changePct >= 0 ? 'up' : 'down'}>{r.changePct}%</td>
              <td>{r.rsi}</td>
              <td>{r.score}</td>
              <td className={r.direction === 'UP' ? 'up' : r.direction === 'DOWN' ? 'down' : ''}>{r.direction}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PositionsPanel({ positions }) {
  if (!positions?.length) {
    return <p className="degen-scan-empty">No open paper positions</p>;
  }
  return (
    <ul className="degen-pos-list degen-pos-list-cards">
      {positions.map((p) => {
        const move = p.currentPrice != null
          ? Math.round((p.side === 'SHORT'
            ? p.entryPrice - p.currentPrice
            : p.currentPrice - p.entryPrice) * 100) / 100
          : null;
        return (
          <li key={p.symbol} className="degen-pos-card">
            <div className="degen-pos-card-head">
              <span className={`degen-exch degen-exch-${(p.exchange || 'NSE').toLowerCase()}`}>{p.exchange}</span>
              <strong>{p.stock}</strong>
              <span>{p.side} ×{p.qty}</span>
            </div>
            <hr className="tab-card-divider cnc-action-divider" aria-hidden="true" />
            <TradingCardWhen item={p} prefix="Opened" />
            <div className="degen-pos-card-body">
              <span>@ ₹{Number(p.entryPrice).toFixed(2)}</span>
              {move != null && (
                <span className={move >= 0 ? 'up' : 'down'}>{move >= 0 ? '+' : ''}₹{move}/sh</span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function DegenDashboard() {
  const [board, setBoard] = useState(defaultBoard);
  const [utc, setUtc] = useState(utcClockString());
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/degen/board', { timeout: 12000 });
      setBoard(data);
      setError(data.ok === false ? (data.error || 'Board error') : null);
    } catch (e) {
      setError(e.message || 'Cannot reach degen backend — is port 9000 running?');
    }
  }, []);

  useEffect(() => {
    refresh();
    const poll = setInterval(refresh, POLL_MS);
    return () => clearInterval(poll);
  }, [refresh]);

  useEffect(() => {
    const clock = setInterval(() => setUtc(utcClockString()), 1000);
    return () => clearInterval(clock);
  }, []);

  const togglePipeline = async () => {
    setBusy(true);
    try {
      const url = board.pipelineRunning ? '/api/degen/stop' : '/api/degen/start';
      const { data } = await axios.post(url);
      setBoard(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const monthLabel = useMemo(() => (board.month || '').toUpperCase(), [board.month]);
  const statusPill = board.marketOpen ? 'LIVE · NSE/BSE' : 'MARKET CLOSED';

  return (
    <div className="degen-app">
      <header className="degen-topbar">
        <div className="degen-top-left">
          <span className="degen-wallet">{formatRs(board.cash)} cash</span>
          <span className="degen-tag degen-tag-mode">{board.mode || 'DEGEN · INTRADAY'}</span>
          <span className="degen-tag degen-tag-pipe">{board.pipelineLabel}</span>
          <span className={`degen-tag degen-tag-live ${board.marketOpen ? 'open' : ''}`}>{statusPill}</span>
        </div>
        <div className="degen-top-right">
          <span className="degen-utc">UTC {utc}</span>
          <span className="degen-utc ist-dt"><IstClock /></span>
          {board.istTime && board.istTime !== '' && (
            <span className="degen-utc degen-utc-muted" title="Last server sync">
              Srv {formatIstDateTime(board.istTime)}
            </span>
          )}
        </div>
      </header>

      {error && (
        <div className="degen-error-banner">{error}</div>
      )}

      <DegenActionScreen
        guide={board.actionGuide}
        marketOpen={board.marketOpen}
        sellAlertsOpen={board.sellAlertsOpen !== false}
        mode="intraday"
        title="Your intraday action screen"
      />

      <section className="degen-hero">
        <div className="degen-hero-main">
          <p className="degen-hero-label">REALIZED P&amp;L · {monthLabel} · PAPER</p>
          <h1 className="degen-hero-pnl">{formatRs(board.realizedPnl)}</h1>
          <div className="degen-hero-stats">
            <span>{board.tradeCount?.toLocaleString()} trades</span>
            <span>{board.winRate}% win</span>
            <span>{board.universeSize} symbols ({board.nseCount} NSE · {board.bseCount} BSE)</span>
          </div>
          <p className="degen-last-action">{board.lastAction}</p>
        </div>
        <WinStreakRing streak={board.winStreak || 0} nextSec={board.nextCycleSec || 0} />
      </section>

      <section className="degen-mid-grid">
        <div className="degen-card degen-cashflow">
          <header>
            <h3>CASH FLOW</h3>
            <span>{monthLabel} · WIN STACK</span>
          </header>
          <p className="degen-card-pnl">{formatRs(board.totalDayPnl ?? board.realizedPnl)}</p>
          <WinStackChart data={board.winStack} />
        </div>

        <div className="degen-card degen-pnl-live">
          <header>
            <h3>24H PNL LIVE</h3>
            <span>Unrealized {formatRs(board.unrealizedPnl || 0)}</span>
          </header>
          <Pnl24Chart series={board.pnl24} />
        </div>
      </section>

      <section className="degen-spot-section degen-card">
        <CandlestickChart candles={board.candles} price={board.spotPrice} symbol={board.spotSymbol} />
      </section>

      <section className="degen-card degen-scan-section">
        <header className="degen-scan-head">
          <h3>NSE + BSE Momentum Scan</h3>
          <span>RSI · VWAP zone · volume · min score {62}</span>
        </header>
        <ScanTable rows={board.scanResults} />
      </section>

      <section className="degen-card degen-pos-section">
        <header className="degen-scan-head">
          <h3>Open Paper Positions</h3>
          <span>Target +0.45% · stop −0.35% · 10 min rotate</span>
        </header>
        <PositionsPanel positions={board.positions} />
      </section>

      <PipelineStrip state={board} />

      <footer className="degen-footer-metrics">
        <div className="degen-metric">
          <span>TRADES · {monthLabel}</span>
          <strong>{board.binsApril ?? board.tradeCount}</strong>
        </div>
        <div className="degen-metric">
          <span>ACTIVE SIGNAL</span>
          <strong className={board.direction === 'UP' ? 'up' : 'down'}>
            {board.lastPick
              ? `${board.lastPick.exchange} ${board.lastPick.stock} ${board.direction}`
              : `${board.direction} · score ${board.leverage}`}
          </strong>
        </div>
        <div className="degen-metric">
          <span>VELOCITY · WIN%</span>
          <strong>{board.velocity1h ?? board.winRate}</strong>
        </div>
        <button type="button" className="degen-toggle" onClick={togglePipeline} disabled={busy}>
          {board.pipelineRunning ? 'Pause pipeline' : 'Start trading'}
        </button>
      </footer>

      {board.activityLog?.length > 0 && (
        <section className="degen-card degen-log-section">
          <h3>Activity</h3>
          <ul className="degen-log">
            {board.activityLog.map((row, i) => (
              <li key={`${row.time}-${i}`} className={row.type}>{formatActivityWhen(row)} — {row.msg}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
